
import { Component, Ref, Vue } from 'vue-property-decorator'
import TopPageLayout from '@/components/templates/TopPageLayout.vue'
import ModalStudentLoginHistory from '@/views/modal/ModalStudentLoginHistory.vue'
import ModalStudentCoinHistory from '@/views/modal/ModalStudentCoinHistory.vue'
import { NoticeMessageData } from '@/models/NoticeMessage'
import { LearningStatusCurriculumUnit, DailyUserReviewProblemCurriculumUnit } from '@/models/CurriculumUnit'
import { ApiNotFoundAppError } from '@/models/AppErrorClasses'
import { LearningProgress } from '@/const/LearningProgress'
import { StudyCategory } from '@/models/StudyCategory'
import { UserCoinsApi } from '@/api/methods/UserCoinsApi'
import { GeneralLearningsApi } from '@/api/methods/GeneralLearningsApi'
import { DailyUserRecommendZnCurriculumUnitsApi } from '@/api/methods/DailyUserRecommendZnCurriculumUnitsApi'
import { DailyUserReviewProblemsApi } from '@/api/methods/DailyUserReviewProblemsApi'
import { FixedExercisesApi } from '@/api/methods/FixedExercisesApi'
import { ZnCurriculumUnitsApi } from '@/api/methods/ZnCurriculumUnitsApi'
import { ApiResponseFixedExerciseProblem } from '@/api/responses/fixedExercises'
import { StudyCategoriesApi } from '@/api/methods/StudyCategoriesApi'
import { NotificationsApi } from '@/api/methods/NotificationsApi'

/**
 * トップページ
 */
@Component({
  components: {
    TopPageLayout,
    ModalStudentLoginHistory,
    ModalStudentCoinHistory,
  },
})
export default class TopPage extends Vue {
  /** ログイン履歴モーダル */
  @Ref()
  modalStudentLoginHistory!: ModalStudentLoginHistory

  /** コイン獲得履歴モーダル */
  @Ref()
  modalStudentCoinHistory!: ModalStudentCoinHistory

  /** 遅延読み込みを行うAPIの最大実行回数 */
  private readonly MAX_TIMES = 10

  /** 遅延読み込みを行うAPIの再実行までの待ち時間(ミリ秒) */
  private readonly WAIT_TIME = 3000

  /** 表示するお知らせ件数 */
  private readonly NOTICE_COUNT = 3

  /** コイン数値 */
  private coinQuantity = 0

  /** 通常授業サポート学習の通算問題演習回数 */
  private countTimesGeneralStudy = 0

  /** 講習会トレーニングの通算問題演習回数 N_LMS-282: 実装時にコメントアウトを外す */
  // private countTimesSeminarStudy = 0

  /** 入試直前トレーニングの通算問題演習回数 N_LMS-283: 実装時にコメントアウトを外す */
  // private countTimesEntranceStudy = 0

  /** お知らせ一覧 */
  private messageList: NoticeMessageData[] = []

  /** おすすめ単元情報(IDは増進堂単元ID) */
  private recommendZnCurriculumUnits: LearningStatusCurriculumUnit[] | null = null

  /** おすすめ単元の取得試行回数 */
  private timesGetRecommendUnits = 0

  /** おすすめ単元再取得処理の識別子(timeoutID) 再取得の解除時に使用する */
  private recommendUnitTimerId = 0

  /** 今日やる問題一覧 */
  private dailyProblemList: DailyUserReviewProblemCurriculumUnit[] | null = null

  /** 今日やる問題の取得回数 */
  private timesGetDailyProblem = 0

  /** 今日やる問題再取得処理の識別子(timeoutID) 再取得の解除時に使用する */
  private dailyProblemTimerId = 0

  /** 通常授業サポート学習教材情報リスト */
  private generalStudyCategoryList: StudyCategory.LearningStatusStudyCategory[] = []

  /** 講習会トレーニング教材情報リスト N_LMS-282: 実装時にコメントアウトを外す */
  // private seminarStudyCategoryList: StudyCategory.LearningStatusStudyCategory[] = []

  /** 入試直前トレーニング教材情報リスト N_LMS-283: 実装時にコメントアウトを外す */
  // private entranceStudyCategoryList: StudyCategory.LearningStatusStudyCategory[] = []

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    // コイン数取得
    const coins = await UserCoinsApi.getUserCoin()
    this.coinQuantity = coins.score

    // 通算演習会数セット(通常授業サポート)
    const generalStudy = await GeneralLearningsApi.getCount()
    this.countTimesGeneralStudy = generalStudy.count

    // 通算演習会数セット(講習会トレーニング)
    // N_LMS-282: 実装時に追加

    // 通算演習会数セット(入試直前トレーニング)
    // N_LMS-283: 実装時に追加

    // お知らせ情報取得
    await this.getNotifications()

    // おすすめ単元を設定する(非同期処理として実行し、完了するまで内部で再帰する)
    this.setRecommendZnCurriculumUnits()

    // 今日やる問題を設定する(完了するまで内部で再帰的処理を行う)
    this.setDailyUserReviewProblems()

    // 教材一覧設定(通常授業サポート)
    this.getGeneralStudyCategoryList()

    // 教材一覧取得(講習会トレーニング)
    // N_LMS-282: 実装時に追加

    // 教材一覧取得(入試直前トレーニング)
    // N_LMS-283: 実装時に追加
  }

  /**
   * おすすめ単元情報を設定する
   */
  private async setRecommendZnCurriculumUnits(): Promise<void> {
    // 試行回数を加算
    this.timesGetRecommendUnits++

    // 選択中の教科を取得
    const subjectCode = await this.$appCookies.studentMenuState.subjectCode.get()
    // おすすめ単元一覧を取得
    const recommendUnits = await DailyUserRecommendZnCurriculumUnitsApi.getDailyUserRecommendZnCurriculumUnits(
      subjectCode
    )

    // レスポンスがnullの場合、データ未生成なので少し待ってから再取得
    if (!recommendUnits) {
      // 取得回数が上限を超えた場合は再取得しない
      if (this.timesGetRecommendUnits > this.MAX_TIMES) {
        return
      }

      // 再取得を予約し、識別ID(戻り値)を保持する
      this.recommendUnitTimerId = setTimeout(() => {
        this.setRecommendZnCurriculumUnits()
      }, this.WAIT_TIME)

      return
    }

    // 取得成功時の処理(front側の型に成形）
    this.recommendZnCurriculumUnits = recommendUnits.map((recommendZnCurriculumUnit) => {
      return {
        id: recommendZnCurriculumUnit.znCurriculumUnitId,
        name: `${recommendZnCurriculumUnit.znCurriculumUnitGroupName} ${recommendZnCurriculumUnit.znCurriculumUnitName}`,
        learningProgress: LearningProgress.findByCode(recommendZnCurriculumUnit.progress),
      }
    })
  }

  /**
   * 今日やる問題情報を設定する
   */
  private async setDailyUserReviewProblems(): Promise<void> {
    // 今日やる問題の取得回数加算
    this.timesGetDailyProblem++

    // 選択中の教科を取得
    const subjectCode = await this.$appCookies.studentMenuState.subjectCode.get()
    // 今日やる問題一覧を取得
    const dailyUserReviewProblems = await DailyUserReviewProblemsApi.getDailyUserReviewProblems(subjectCode)

    // レスポンスがnullの場合、再取得
    if (!dailyUserReviewProblems) {
      // 取得回数が10回を超えたら終了
      if (this.timesGetDailyProblem > this.MAX_TIMES) {
        return
      }

      // 再取得を予約し、識別ID(戻り値)を保持する
      this.dailyProblemTimerId = setTimeout(() => {
        this.setDailyUserReviewProblems()
      }, this.WAIT_TIME)

      return
    }

    // 取得成功時の処理(front側の型に成形）
    this.dailyProblemList = dailyUserReviewProblems!.map((problem) => {
      return {
        id: problem.curriculumUnitId,
        name: problem.curriculumUnitName,
        problemCode: problem.problemCode,
        level: problem.level,
      }
    })
  }

  /**
   * コインボタン押下時の処理
   */
  private async eventClickCoinButton(): Promise<void> {
    await this.modalStudentCoinHistory.show()
  }

  /**
   * 履歴ボタン押下時の処理
   */
  private async eventClickHistoryButton(): Promise<void> {
    await this.modalStudentLoginHistory.show()
  }

  /**
   * 通常授業サポート学習の詳細ボタン押下時の処理
   */
  private eventClickGeneralButton(): void {
    this.$router.push('/student/general')
  }

  /**
   * 講習会トレーニングの詳細ボタン押下時の処理
   *
   * N_LMS-282: 実装時にコメントアウトを外す
   */
  // private eventClickSeminarButton(): void {
  //   this.$router.push('/student/seminar')
  // }

  /**
   * 入試直前トレーニングの詳細ボタン押下時の処理
   *
   * N_LMS-283: 実装時にコメントアウトを外す
   */
  // private eventClickEntranceButton(): void {
  //   this.$router.push('/student/entrance')
  // }

  /**
   * お知らせボックス押下時のイベント
   *
   * @param id お知らせID
   */
  private eventClickNotice(id: number): void {
    this.$router.push(`/student/notice/${id}`)
  }

  /**
   * apiから新着メッセージのリストを取得する
   */
  private async getNotifications(): Promise<void> {
    const searchResult = await NotificationsApi.getNotifications(this.NOTICE_COUNT)

    // お知らせ情報を設定
    this.messageList = searchResult.notifications.map((noticeDetail) => {
      return {
        id: noticeDetail.id,
        type: noticeDetail.type,
        title: noticeDetail.title,
        startedDate: new Date(noticeDetail.startedAt * 1000),
        endedDate: new Date(noticeDetail.endedAt * 1000),
        description: noticeDetail.message,
        updatedUser: noticeDetail.updatedBy,
        updatedAt: new Date(noticeDetail.updatedAt * 1000),
      }
    })
  }

  /**
   * 知識マップのおすすめ単元クリック時の処理
   *
   * @param id 増進堂単元ID
   */
  private async eventClickRecommendZnCurriculumUnit(id: number): Promise<void> {
    // 増進堂単元と紐付く日能研単元情報を取得
    const znUnitConvert = await ZnCurriculumUnitsApi.getZnCurriculumUnitConvert(id)

    if (znUnitConvert.units.length === 1) {
      // 単独の日能研単元に紐付く場合、問題選択画面に遷移
      this.$router.push(`/student/general/${znUnitConvert.units[0].id}`)
    } else {
      // 複数の日能研単元に紐付く場合、単元選択画面に遷移
      this.$router.push(`/student/map/${id}`)
    }
  }

  /**
   * 今日やる問題の項目ボックスクリック時の処理
   *
   * @param curriculumUnitId 単元ID
   */
  private async eventClickDailyProblem(curriculumUnitId: number): Promise<void> {
    // 単元IDから該当の今日やる問題情報を取得
    const dailyReviewProblem = this.dailyProblemList?.find((item) => item.id === curriculumUnitId)

    // undefinedケース、発生しないがブロックの為記述
    if (!dailyReviewProblem) {
      alert('指定された問題がありません。')
      return
    }

    // 固定問題情報を取得
    const fixedExercise = await FixedExercisesApi.getActive(curriculumUnitId, dailyReviewProblem.level)

    // 小問情報を取得
    let problem!: ApiResponseFixedExerciseProblem
    try {
      problem = await FixedExercisesApi.getProblem(fixedExercise.id, dailyReviewProblem.problemCode)
    } catch (error) {
      if (error instanceof ApiNotFoundAppError) {
        alert('この問題は現在演習できません。')
        this.$loading.complete()
        return
      }

      // 404エラー以外の場合は例外を投げ直し、共通処理に渡す
      throw error
    }

    this.$router.push(`/student/review/study/${fixedExercise.id}/${problem.id}`)
  }

  /**
   * 通常授業サポート学習教材情報リストを設定
   */
  private async getGeneralStudyCategoryList(): Promise<void> {
    // 教科と学年コードを取得
    const subjectCode = await this.$appCookies.studentMenuState.subjectCode.get()
    const gradeCode = await this.$appCookies.studentMenuState.gradeCode.get()
    // 教材一覧取得
    const studyCategories = await StudyCategoriesApi.getGeneralProgresses(gradeCode, subjectCode)
    // front側の型に成形し代入
    this.generalStudyCategoryList = studyCategories.map((studyCategory) => {
      let history = null
      if (studyCategory.unixLearnedAt !== null) {
        history = new Date(studyCategory.unixLearnedAt * 1000)
      }
      return {
        id: studyCategory.id,
        name: studyCategory.name,
        code: studyCategory.code,
        textbookImagePath: studyCategory.textbookImagePath,
        progressRate: studyCategory.progressRate * 100,
        historyDate: history,
      }
    })
  }

  /**
   * 通常授業サポート学習教材クリック時の処理
   *
   * @param studyCategoryId 教材ID
   */
  private eventClickGeneralStudyCategory(studyCategoryId: number): void {
    this.$router.push({
      path: '/student/general',
      query: { studyCategoryId: `${studyCategoryId}` },
    })
  }

  /**
   * 講習会トレーニング教材情報リスト取得
   *
   * N_LMS-282: 実装後にコメントアウトを外し、api実装
   */
  // private getSeminarStudyCategoryList(): void {
  //   // ダミーデータ
  //   this.seminarStudyCategoryList = []
  // }

  /**
   * 講習会トレーニング教材クリック時の処理
   *
   * N_LMS-282: 実装時にコメントアウトを外す
   *
   * @param id 教材ID
   */
  // private eventClickSeminarStudyCategory(id: number): void {
  //   alert(`ID:${id}の講習会トレーニング教材`)
  // }

  /**
   * 入試直前トレーニング教材情報リスト取得
   *
   * N_LMS-283: 実装時にコメントアウトを外し、api実装
   */
  // private getEntranceStudyCategoryList(): void {
  //   // ダミーデータ
  //   this.entranceStudyCategoryList = []
  // }

  /**
   * 入試直前トレーニング教材クリック時の処理
   *
   * N_LMS-283: 実装時にコメントアウトを外す
   *
   * @param id 教材ID
   */
  // private eventClickEntranceStudyCategory(id: number): void {
  //   alert(`ID:${id}の入試直前トレーニング教材`)
  // }

  /**
   * 画面遷移時の処理
   */
  private beforeDestroy(): void {
    // おすすめ単元再取得の解除
    clearTimeout(this.recommendUnitTimerId)
    // 今日やる問題再取得の解除
    clearTimeout(this.dailyProblemTimerId)
  }
}
